import { createAnalytics, createBrowserAnalyticsRuntime, CONSENT_STORAGE_KEY, type ConsentChoice } from "./analytics";
import { captureFirstTouch, resolveMessageMatch, type LandingAttribution } from "./attribution";
import { landingConfig } from "./config";
import { buildForecastView, formatRupees, type ForecastHorizon } from "./forecast-demo";
import { submitLead } from "./lead-form";

const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const analytics = createAnalytics(landingConfig.analytics, createBrowserAnalyticsRuntime());

function readStoredConsent(): ConsentChoice {
  try {
    const value = localStorage.getItem(CONSENT_STORAGE_KEY);
    return value === "accepted" || value === "rejected" ? value : "unset";
  } catch {
    return "unset";
  }
}

function persistConsent(choice: Exclude<ConsentChoice, "unset">): void {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, choice);
  } catch {
    // Consent still applies to this page view when storage is unavailable.
  }
}

function setupConsent(): void {
  const control = document.querySelector<HTMLElement>("[data-consent-control]");
  const manageButtons = document.querySelectorAll<HTMLButtonElement>("[data-consent-manage]");
  const setControlOpen = (open: boolean, moveFocus = false) => {
    control?.toggleAttribute("hidden", !open);
    manageButtons.forEach((button) => button.setAttribute("aria-expanded", String(open)));
    if (open && moveFocus) {
      control?.querySelector<HTMLButtonElement>("[data-consent-reject]")?.focus();
    }
  };

  manageButtons.forEach((button) => {
    button.setAttribute("aria-expanded", "false");
    button.addEventListener("click", () => setControlOpen(true, true));
  });

  control?.querySelector("[data-consent-accept]")?.addEventListener("click", () => {
    persistConsent("accepted");
    analytics.setConsent("accepted");
    setControlOpen(false);
  });
  control?.querySelector("[data-consent-reject]")?.addEventListener("click", () => {
    persistConsent("rejected");
    analytics.setConsent("rejected");
    setControlOpen(false);
  });

  const stored = readStoredConsent();
  if (stored !== "unset") {
    analytics.setConsent(stored);
    setControlOpen(false);
    return;
  }

  setControlOpen(true);
}

function getAttribution(): LandingAttribution {
  try {
    return captureFirstTouch({
      storage: localStorage,
      now: Date.now(),
      search: window.location.search,
      path: `${window.location.pathname}${window.location.search}`,
      referrer: document.referrer,
    });
  } catch {
    return { landing_path: `${window.location.pathname}${window.location.search}` };
  }
}

function applyMessageMatch(): void {
  const match = resolveMessageMatch(window.location.search);
  if (!match) return;
  const support = document.querySelector<HTMLElement>("[data-message-support]");
  const outcome = document.querySelector<HTMLElement>("[data-message-outcome]");
  if (support) support.textContent = match.support;
  if (outcome) outcome.textContent = match.outcome;
}

function setupLeadForms(attribution: LandingAttribution): void {
  let formStarted = false;
  document.querySelectorAll<HTMLFormElement>("[data-lead-form]").forEach((form) => {
    const email = form.querySelector<HTMLInputElement>("[data-lead-email]");
    const submit = form.querySelector<HTMLButtonElement>("[data-lead-submit]");
    const status = form.querySelector<HTMLElement>("[data-form-status]");
    if (!email || !submit || !status) return;

    email.addEventListener("focus", () => {
      if (formStarted) return;
      formStarted = true;
      analytics.track("lead_form_start");
    });

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      status.className = "form-status";
      status.textContent = "";
      submit.disabled = true;
      submit.textContent = "Sending...";

      const result = await submitLead(
        { email: email.value, attribution },
        {
          endpoint: `${landingConfig.apiBaseUrl}/api/v1/leads`,
          fetch: (url, init) => window.fetch(url, init),
          track: (name, params) => analytics.track(name, params),
          trackCreatedConversion: () => analytics.trackCreatedLead(),
        },
      );

      status.className = `form-status is-visible ${result.status === "created" || result.status === "already_joined" ? "is-success" : "is-error"}`;
      status.textContent = result.message;
      if (result.status === "invalid") email.focus();
      if (result.status === "created" || result.status === "already_joined") form.reset();
      submit.disabled = false;
      submit.textContent = "Get early access";
    });
  });
}

function pointPath(values: number[], width: number, height: number, min: number, max: number): string {
  return values
    .map((value, index) => {
      const x = 54 + (index / Math.max(1, values.length - 1)) * (width - 108);
      const y = 24 + ((max - value) / Math.max(1, max - min)) * (height - 64);
      return `${index === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
}

function setupForecastDemo(): void {
  const demo = document.querySelector<HTMLElement>("[data-forecast-demo]");
  if (!demo) return;

  const line = demo.querySelector<SVGPathElement>("[data-series-line]");
  const band = demo.querySelector<SVGPolygonElement>("[data-confidence-band]");
  const pointGroup = demo.querySelector<SVGGElement>("[data-plot-points]");
  const pointList = demo.querySelector<HTMLElement>("[data-point-list]");
  const drivers = demo.querySelector<HTMLElement>("[data-driver-list]");
  const range = demo.querySelector<HTMLElement>("[data-forecast-range]");
  const total = demo.querySelector<HTMLElement>("[data-forecast-total]");
  const horizonLabel = demo.querySelector<HTMLElement>("[data-horizon-label]");
  const detail = demo.querySelector<HTMLElement>("[data-inspection-detail]");
  const replay = demo.querySelector<HTMLButtonElement>("[data-replay]");
  const basis = demo.querySelector<HTMLDetailsElement>("[data-basis]");
  const motionStatus = demo.querySelector<HTMLElement>("[data-motion-status]");
  let currentHorizon: ForecastHorizon = "month";

  const render = (horizon: ForecastHorizon) => {
    const view = buildForecastView(horizon);
    currentHorizon = horizon;
    const futureMonthly = Math.round(view.result.projected / view.monthsAhead);
    const futureLow = Math.round(view.result.confidenceLow / view.monthsAhead);
    const futureHigh = Math.round(view.result.confidenceHigh / view.monthsAhead);
    const historical = view.history.map((month) => month.total);
    const values = [...historical, ...Array.from({ length: view.monthsAhead }, () => futureMonthly)];
    const min = Math.min(...historical, futureLow) - 1_500;
    const max = Math.max(...historical, futureHigh) + 1_500;
    const width = 960;
    const height = 300;

    if (line) line.setAttribute("d", pointPath(values, width, height, min, max));
    if (band) {
      const startIndex = historical.length - 1;
      const xFor = (index: number) => 54 + (index / Math.max(1, values.length - 1)) * (width - 108);
      const yFor = (value: number) => 24 + ((max - value) / Math.max(1, max - min)) * (height - 64);
      const lowPoints = [
        `${xFor(startIndex).toFixed(1)},${yFor(historical.at(-1) || futureMonthly).toFixed(1)}`,
        ...Array.from({ length: view.monthsAhead }, (_, index) =>
          `${xFor(historical.length + index).toFixed(1)},${yFor(futureLow).toFixed(1)}`,
        ),
      ];
      const highPoints = [
        ...Array.from({ length: view.monthsAhead }, (_, index) =>
          `${xFor(historical.length + view.monthsAhead - index - 1).toFixed(1)},${yFor(futureHigh).toFixed(1)}`,
        ),
        `${xFor(startIndex).toFixed(1)},${yFor(historical.at(-1) || futureMonthly).toFixed(1)}`,
      ];
      band.setAttribute("points", [...lowPoints, ...highPoints].join(" "));
    }

    if (pointGroup) {
      const pointMarkup = values.map((value, index) => {
        const x = 54 + (index / Math.max(1, values.length - 1)) * (width - 108);
        const y = 24 + ((max - value) / Math.max(1, max - min)) * (height - 64);
        return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${index >= historical.length ? 7 : 5}" class="${index >= historical.length ? "is-forecast" : ""}"></circle>`;
      }).join("");
      pointGroup.innerHTML = pointMarkup;
    }

    if (pointList) {
      const historyButtons = view.history.map((month) => {
        const label = new Intl.DateTimeFormat("en-IN", { month: "short" }).format(new Date(`${month.month}-01T00:00:00Z`));
        return `<button type="button" data-point-label="${label}" data-point-value="${month.total}"><span>${label}</span><strong>${formatRupees(month.total)}</strong></button>`;
      });
      const forecastButtons = Array.from({ length: view.monthsAhead }, (_, index) =>
        `<button type="button" class="is-forecast" data-point-label="Forecast ${index + 1}" data-point-value="${futureMonthly}"><span>${view.monthsAhead === 1 ? "Forecast" : `Month ${index + 1}`}</span><strong>${formatRupees(futureMonthly)}</strong></button>`,
      );
      pointList.innerHTML = [...historyButtons, ...forecastButtons].join("");
      pointList.querySelectorAll<HTMLButtonElement>("button").forEach((button) => {
        button.addEventListener("click", () => {
          pointList.querySelectorAll("button").forEach((item) => item.classList.remove("is-active"));
          button.classList.add("is-active");
          if (detail) detail.textContent = `${button.dataset.pointLabel}: ${formatRupees(Number(button.dataset.pointValue || 0))}`;
          analytics.track("forecast_point_view", { horizon: currentHorizon, point: button.dataset.pointLabel });
        });
      });
    }

    if (drivers) {
      drivers.innerHTML = view.drivers.map((driver) =>
        `<button type="button" data-driver-id="${driver.id}" data-driver-detail="${driver.explanation}"><span>${driver.label}</span><strong>${formatRupees(driver.amount)}</strong></button>`,
      ).join("");
      drivers.querySelectorAll<HTMLButtonElement>("button").forEach((button) => {
        button.addEventListener("click", () => {
          drivers.querySelectorAll("button").forEach((item) => item.classList.remove("is-active"));
          button.classList.add("is-active");
          if (detail) detail.textContent = button.dataset.driverDetail || "";
          analytics.track("forecast_driver_view", { horizon: currentHorizon, driver: button.dataset.driverId });
        });
      });
    }

    if (range) range.textContent = `${formatRupees(view.result.confidenceLow)} to ${formatRupees(view.result.confidenceHigh)}`;
    if (total) total.textContent = formatRupees(view.result.projected);
    if (horizonLabel) horizonLabel.textContent = view.label;
    if (detail) detail.textContent = "Range shown with named drivers and a visible calculation basis.";
  };

  demo.querySelectorAll<HTMLButtonElement>("[data-horizon]").forEach((button) => {
    button.addEventListener("click", () => {
      const horizon = button.dataset.horizon === "quarter" ? "quarter" : "month";
      if (horizon === currentHorizon) return;
      demo.querySelectorAll<HTMLButtonElement>("[data-horizon]").forEach((item) => {
        const selected = item === button;
        item.classList.toggle("is-active", selected);
        item.setAttribute("aria-pressed", String(selected));
      });
      render(horizon);
      analytics.track("forecast_horizon_change", { horizon });
    });
  });

  replay?.addEventListener("click", () => {
    if (reducedMotion) {
      if (motionStatus) motionStatus.textContent = "Forecast is already shown in its final state because reduced motion is on.";
      return;
    }
    demo.classList.remove("is-replaying");
    window.requestAnimationFrame(() => demo.classList.add("is-replaying"));
    window.setTimeout(() => demo.classList.remove("is-replaying"), 1_200);
    analytics.track("forecast_demo_start", { source: "replay" });
  });

  basis?.addEventListener("toggle", () => {
    if (basis.open) analytics.track("forecast_basis_open", { horizon: currentHorizon });
  });

  let demoStarted = false;
  const markDemoStarted = () => {
    if (demoStarted) return;
    demoStarted = true;
    analytics.track("forecast_demo_start", { source: "view" });
  };
  if ("IntersectionObserver" in window) {
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      markDemoStarted();
      observer.disconnect();
    }, { threshold: 0.25 });
    observer.observe(demo);
  } else {
    markDemoStarted();
  }

  render("month");
}

function setupReveal(): void {
  const items = document.querySelectorAll<HTMLElement>("[data-reveal]");
  if (reducedMotion || !("IntersectionObserver" in window)) {
    items.forEach((item) => item.classList.add("is-visible"));
    return;
  }
  document.documentElement.classList.add("motion-ready");
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      (entry.target as HTMLElement).classList.add("is-visible");
      observer.unobserve(entry.target);
    });
  }, { rootMargin: "0px 0px -48px", threshold: 0.12 });
  items.forEach((item) => observer.observe(item));
}

function setupNavigation(): void {
  document.querySelectorAll<HTMLAnchorElement>('a[href^="#"]').forEach((anchor) => {
    anchor.addEventListener("click", (event) => {
      const selector = anchor.getAttribute("href");
      if (!selector || selector === "#") return;
      const target = document.querySelector(selector);
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" });
    });
  });
}

setupReveal();
setupConsent();
const attribution = getAttribution();
applyMessageMatch();
setupLeadForms(attribution);
setupForecastDemo();
setupNavigation();
